/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from 'react';
import {
  Class,
  ClassStatus,
  Coach,
  PaginatedResponse,
  Subtype,
  Response,
  ServiceType,
  SuccessResponse,
} from '../types/ClassTable.type';
import type { ClassCalendarFilters } from '../types/ClassCalendar.type';
import dayjs from 'dayjs';
import { apiDelete, apiGet } from '../utils/apiClient';
import { SelectChangeEvent } from '@mui/material';
import { notifyAPIClient, notifyFailure } from '../utils/toast';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { exportRowsToXlsx } from '../utils/xlsxExport';
import { appendClassCalendarFilters } from '../utils/classCalendarQuery';

type UseClassTableOptions = {
  enabled?: boolean;
};

export const useClassTable = ({
  enabled = true,
}: UseClassTableOptions = {}) => {
  // Data states
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);

  // Toggle states
  const [openCreateClassDialog, setOpenCreateClassDialog] = useState(false);
  const [openQuickAddClassDialog, setOpenQuickAddClassDialog] = useState(false);
  const [openClassInfoDialog, setOpenClassInfoDialog] = useState(false);
  const [openDeleteClassDialog, setOpenDeleteClassDialog] = useState(false);
  const [openClassMembersDialog, setOpenClassMembersDialog] = useState(false);
  const [deleteWeekLoading, setDeleteWeekLoading] = useState(false);

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter states
  const [serviceOptions, setServiceOptions] = useState<ServiceType[]>([]);
  const [subTypeFilter, setSubTypeFilter] = useState<Subtype>({
    id: -1,
    name: 'All',
  });
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [levelFilter, setLevelFilter] = useState<string>('All');
  const [capacityFilter, setCapacityFilter] = useState<
    'All' | 'AVAILABLE' | 'FULL'
  >('All');
  const [dateRangeFilter, setDateRangeFilter] = useState<{
    from: string;
    to: string;
  }>({ from: '', to: '' });
  const [dayOfWeekFilter, setDayOfWeekFilter] = useState<string[]>([]);
  const [timeSlotFilter, setTimeSlotFilter] = useState<string>('All');
  const [coachFilter, setCoachFilter] = useState<string>('All');
  const [coachOptions, setCoachOptions] = useState<Coach[]>([]);

  // Last sync time for assessment data
  const [lastSyncTime, setLastSyncTime] = useState<dayjs.Dayjs | null>(null);

  // Highlighted row
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  // View mode state
  const [viewMode, setViewMode] = useState<'all' | 'grouped'>('all');
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [exportWeekLoading, setExportWeekLoading] = useState(false);

  // Data-loading state for the main list fetch
  const [loading, setLoading] = useState(true);

  // Networking
  const fetchClasses = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        // Build query parameters
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          search: searchTerm,
          subtype: subTypeFilter?.name || '',
          status: statusFilter,
        });

        // Add level filter
        if (levelFilter && levelFilter !== 'All') {
          params.append('level', levelFilter);
        }

        // Add date range filters
        if (dateRangeFilter.from) {
          params.append('dateFrom', dateRangeFilter.from);
        }
        if (dateRangeFilter.to) {
          params.append('dateTo', dateRangeFilter.to);
        }

        // Add day of week filter
        if (dayOfWeekFilter.length > 0) {
          params.append('daysOfWeek', dayOfWeekFilter.join(','));
        }

        // Add time slot filter
        if (timeSlotFilter && timeSlotFilter !== 'All') {
          params.append('timeSlot', timeSlotFilter);
        }
        if (coachFilter !== 'All') {
          params.append('coachId', coachFilter);
        }
        if (capacityFilter !== 'All') {
          params.append('capacity', capacityFilter);
        }

        const res = await apiGet<PaginatedResponse<Class>>(
          `/class/list?${params.toString()}`,
        );

        setClasses(res.data);
        setTotalItems(res.metadata.totalItems);
        setLastSyncTime(dayjs());
      } catch (error) {
        console.error('Failed to fetch classes:', error);
      } finally {
        setLoading(false);
      }
    },
    [
      page,
      limit,
      searchTerm,
      subTypeFilter?.name,
      statusFilter,
      levelFilter,
      dateRangeFilter,
      dayOfWeekFilter,
      timeSlotFilter,
      coachFilter,
      capacityFilter,
    ],
  );

  const fetchServicesOptions = async () => {
    // Fetch service types from the API
    const response = await apiGet<Response<ServiceType>>(
      '/service/filter?name=COURSE',
    );

    setServiceOptions(response.data);
  };

  // Handle logic
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleFilterSubtypeChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    const selectedSubtype = serviceOptions[0]?.subtypes.find(
      (option) => option.id === Number(newValue),
    ) || {
      id: -1,
      name: 'All',
    };

    setSubTypeFilter(selectedSubtype);
    setPage(1);
  };

  const handleCloseClassInfoDialog = () => {
    setSelectedClass(null);
    setOpenClassInfoDialog(false);
  };

  const handleDeleteClass = async (record: Class) => {
    try {
      const res = await apiDelete<SuccessResponse>(
        `/class/delete/${record.id}?force=${false}`,
      );
      notifyAPIClient(res.message);
      setSelectedClass(null);
      fetchClasses();
    } catch (error) {
      console.error('Failed to delete class:', error);
    }
  };

  const handleDeleteWeek = async (startDate: string): Promise<boolean> => {
    if (deleteWeekLoading) return false;

    setDeleteWeekLoading(true);
    try {
      const res = await apiDelete<SuccessResponse>(
        `/class/delete-week/${startDate}`,
      );
      notifyAPIClient(res.message);
      await fetchClasses();
      return true;
    } catch (error) {
      console.error('Failed to delete week classes:', error);
      notifyFailure();
      return false;
    } finally {
      setDeleteWeekLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSubTypeFilter({ id: -1, name: 'All' });
    setStatusFilter('All');
    setLevelFilter('All');
    setCapacityFilter('All');
    setDateRangeFilter({ from: '', to: '' });
    setDayOfWeekFilter([]);
    setTimeSlotFilter('All');
    setCoachFilter('All');
    setSelectedClass(null);

    setPage(1);
    setLimit(10);
  };

  const handleExportWeekReport = async (
    startDate: string,
    filters?: ClassCalendarFilters,
  ) => {
    if (exportWeekLoading) return;
    setExportWeekLoading(true);
    try {
      // Get the authentication token
      const tokens = JSON.parse(
        localStorage.getItem('aquatics_hub_2025_tokens') || 'null',
      );

      if (!tokens || !tokens.accessToken) {
        notifyAPIClient('Vui lòng đăng nhập để xuất báo cáo');
        return;
      }

      const exportFilters = filters || {
        search: searchTerm,
        subtype: subTypeFilter?.name || 'All',
        status: statusFilter,
        level: levelFilter,
        capacity: capacityFilter,
        timeSlot: timeSlotFilter,
        coachId: coachFilter === 'All' ? undefined : coachFilter,
        daysOfWeek: dayOfWeekFilter.length
          ? dayOfWeekFilter.join(',')
          : undefined,
        date: undefined,
        dateFrom: dateRangeFilter.from || undefined,
        dateTo: dateRangeFilter.to || undefined,
      };
      const params = appendClassCalendarFilters(
        new URLSearchParams(),
        exportFilters,
      );

      // Make API call to download the Excel file
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/class/export-week-report/${startDate}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
          },
          responseType: 'blob', // Important for file download
        },
      );

      // Create blob from response
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Generate filename
      const subtype = exportFilters.subtype || 'all';
      const filename = `weekly_class_sheet_${subtype.toLowerCase()}_${startDate}.xlsx`;

      // Save file
      saveAs(blob, filename);
      notifyAPIClient('Export successful!');
    } catch (error: any) {
      console.error('Failed to export week report:', error);
      if (error.response?.status === 401) {
        notifyAPIClient('You do not have permission to export the report');
      } else if (error.response?.status === 404) {
        notifyAPIClient('No data found for this week');
      } else {
        notifyAPIClient('Export failed. Please try again!');
      }
    } finally {
      setExportWeekLoading(false);
    }
  };

  const handleDownloadClasses = async () => {
    if (totalItems === 0) return;

    setDownloadLoading(true);
    try {
      // The weekday filter is applied after pagination by the API, so use a
      // larger page size in that case to include every matching class.
      const exportLimit =
        dayOfWeekFilter.length > 0 ? Math.max(totalItems, 10000) : totalItems;
      const params = new URLSearchParams({
        page: '1',
        limit: exportLimit.toString(),
        search: searchTerm,
        subtype: subTypeFilter?.name || 'All',
        status: statusFilter,
      });

      if (levelFilter && levelFilter !== 'All') {
        params.append('level', levelFilter);
      }
      if (dateRangeFilter.from) {
        params.append('dateFrom', dateRangeFilter.from);
      }
      if (dateRangeFilter.to) {
        params.append('dateTo', dateRangeFilter.to);
      }
      if (dayOfWeekFilter.length > 0) {
        params.append('daysOfWeek', dayOfWeekFilter.join(','));
      }
      if (timeSlotFilter && timeSlotFilter !== 'All') {
        params.append('timeSlot', timeSlotFilter);
      }
      if (coachFilter !== 'All') {
        params.append('coachId', coachFilter);
      }
      if (capacityFilter !== 'All') {
        params.append('capacity', capacityFilter);
      }

      const response = await apiGet<PaginatedResponse<Class>>(
        `/class/list?${params.toString()}`,
      );

      const rows = response.data.map((record) => ({
        ID: record.id,
        'Class Name': record.name,
        Date: record.classDate
          ? new Date(record.classDate).toLocaleDateString('en-GB')
          : '---',
        Time: `${record.startTime || '---'} - ${record.endTime || '---'}`,
        'Class Type': record.subtypeName,
        Level: record.level || '---',
        'Coach Teacher': record.coachTeachers?.join(', ') || '---',
        Status:
          record.status === ClassStatus.ACTIVE
            ? 'Active'
            : record.status === ClassStatus.CLOSED
              ? 'Closed'
              : 'Cancelled',
        Note: record.note || '---',
      }));

      exportRowsToXlsx(
        rows,
        `classes_${dayjs().format('YYYY-MM-DD')}.xlsx`,
        Date.now().toString(),
      );
    } catch (error) {
      console.error('Failed to download classes:', error);
      notifyAPIClient('Failed to download classes. Please try again later.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const fetchCoachOptions = useCallback(async () => {
    try {
      const response = await apiGet<Response<Coach>>('/class/coach/list-all');
      setCoachOptions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch coach options:', error);
    }
  }, []);

  // Handle useEffect
  useEffect(() => {
    if (!enabled) return;

    fetchClasses();
  }, [
    enabled,
    page,
    limit,
    subTypeFilter,
    setSubTypeFilter,
    statusFilter,
    levelFilter,
    dateRangeFilter,
    dayOfWeekFilter,
    timeSlotFilter,
    coachFilter,
    capacityFilter,
  ]);

  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => {
      fetchClasses(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [enabled, fetchClasses]);

  useEffect(() => {
    fetchServicesOptions();
  }, []);

  useEffect(() => {
    fetchCoachOptions();
  }, [fetchCoachOptions]);

  const emptyRows = limit - classes.length;

  return {
    classes,
    selectedClass,
    setSelectedClass,

    page,
    setPage,
    limit,
    totalItems,

    searchTerm,
    subTypeFilter,
    setSubTypeFilter,
    statusFilter,
    setStatusFilter,
    levelFilter,
    setLevelFilter,
    capacityFilter,
    setCapacityFilter,
    dateRangeFilter,
    setDateRangeFilter,
    dayOfWeekFilter,
    setDayOfWeekFilter,
    timeSlotFilter,
    setTimeSlotFilter,
    coachFilter,
    setCoachFilter,
    coachOptions,
    serviceOptions,

    fetchClasses,
    openCreateClassDialog,
    setOpenCreateClassDialog,
    openQuickAddClassDialog,
    setOpenQuickAddClassDialog,
    openClassInfoDialog,
    setOpenClassInfoDialog,
    handleCloseClassInfoDialog,
    handleDeleteClass,
    handleDeleteWeek,
    deleteWeekLoading,
    openDeleteClassDialog,
    setOpenDeleteClassDialog,
    openClassMembersDialog,
    setOpenClassMembersDialog,

    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    clearFilters,
    handleFilterSubtypeChange,
    handleExportWeekReport,
    exportWeekLoading,
    handleDownloadClasses,
    downloadLoading,

    emptyRows,
    lastSyncTime,
    highlightedRow,
    setHighlightedRow,
    viewMode,
    setViewMode,
    loading,
  };
};
